import { Theme, useTheme } from "@/components/theme/themeProvider"
import { DropdownMenuItem } from "@/components/ui/baseUi/dropdown-menu"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { useUser } from "@/modules/users/hooks/useUser"
import { TrashIcon } from "@images/svgIcons/trashIcon"
import { useCurrentBox } from "@modules/box/hooks/useBox"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { word } from "@modules/core/utils/i18n"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { BoxOfferReductionItem } from "@modules/promoCode/components/boxOfferReductionItem"
import { ProgramReductionItem } from "@modules/promoCode/components/programReductionItem"
import { BoxPromoCodeEntity } from "@modules/promoCode/entities/boxPromoCodeEntity"
import { PromoCodeEntity } from "@modules/promoCode/entities/promoCodeEntity"
import { useBoxPromoCodesDelete } from "@modules/promoCode/hooks/useBoxPromoCodesActions"
import { usePromoCodesDelete } from "@modules/promoCode/hooks/usePromoCodesActions"
import { usePromoCodeSidebar } from "@modules/promoCode/hooks/usePromoCodeSidebar"
import {
  getFormattedPromoCodeExpiration,
  getFormattedPromoCodeReduction,
  getFormattedPromoCodeStartAt,
} from "@modules/promoCode/utils/promoCodeUtils"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { EllipsisMenu } from "@modules/ui/components/ellipsisMenu"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { EditIcon } from "@modules/ui/icons/editIcon"
import { defaultDateFormat } from "@modules/utils/dateUtils"
import { shortenText } from "@modules/utils/stringUtils"
import dayjs from "dayjs"
import { Clock2, ClockAlert, ClockFading, Files, Funnel, Percent, Users, ZapIcon } from "lucide-react"
import { useEffect } from "react"

type PromoCodeInfoResumeProps = {
  promoCode: PromoCodeEntity | BoxPromoCodeEntity
  className?: string
} & React.ComponentProps<"div">

export function PromoCodeInfoResume({ promoCode, className, ...props }: PromoCodeInfoResumeProps) {
  const language = useLanguage()
  const confirmPopup = useConfirmPopup()
  const { result: box } = useCurrentBox()
  const hasEditFinancialPermission = useBoxStaffPermission(BoxStaffPermissions.EDIT_FINANCIAL)
  const canEditFinancial = !box || hasEditFinancialPermission
  const user = useUser()
  const { theme } = useTheme()
  const { setCustomHeaderButtons } = useSidebar()
  const { navigateToPromoCodeFormView, navigateToPromoCodeListView } = usePromoCodeSidebar()
  const { deleteBoxPromoCode } = useBoxPromoCodesDelete()
  const { deletePromoCode } = usePromoCodesDelete()

  const copyCodeToClipboard = async () => {
    await navigator.clipboard.writeText(promoCode.code)
    toast.success(word("promoCode.view.copyCode.success"), { classNames: successToastClassName() })
  }

  const deletePromoCodeConfirmation = () => {
    confirmPopup.show({
      title: word("promoCode.view.deleteConfirm.title"),
      message: word("promoCode.view.deleteConfirm.message"),
      accept: () => {
        if (promoCode instanceof BoxPromoCodeEntity) {
          deleteBoxPromoCode(promoCode.id)
            .then(() => {
              navigateToPromoCodeListView()
              toast.success(word("promoCode.view.deleteConfirm.success"), { classNames: successToastClassName() })
            })

            .catch((e: unknown) => {
              const message = e instanceof Error ? e.message : String(e)
              toast.error(word("global.error.label"), { description: message, classNames: errorToastClassName() })
            })
        } else {
          deletePromoCode(promoCode.id)
            .then(() => {
              navigateToPromoCodeListView()
              toast.success(word("promoCode.view.deleteConfirm.success"), { classNames: successToastClassName() })
            })
            .catch((e: unknown) => {
              const message = e instanceof Error ? e.message : String(e)
              toast.error(word("global.error.label"), { description: message, classNames: errorToastClassName() })
            })
        }
      },
    })
  }

  useEffect(() => {
    if (!canEditFinancial) return
    const canBeEdited =
      !promoCode.archived &&
      (!promoCode.expirationDate || !dayjs(promoCode.expirationDate).isBefore(dayjs().format(defaultDateFormat)))
    const canBeDeleted = !promoCode.archived

    const editionSubMenu = (
      <DropdownMenuItem
        key="edit"
        onClick={() => {
          navigateToPromoCodeFormView(promoCode)
        }}
      >
        <EditIcon color="currentColor" width={20} height={20} className="text-textDefault-body" />
        {word("promoCode.view.edit")}
      </DropdownMenuItem>
    )

    const deletionSubMenu = (
      <DropdownMenuItem
        key="delete"
        onClick={() => {
          deletePromoCodeConfirmation()
        }}
      >
        <TrashIcon color="currentColor" width={20} height={20} className="text-textDefault-body" />
        {word("promoCode.view.delete")}
      </DropdownMenuItem>
    )

    const ellipsisMenu = (
      <EllipsisMenu propsContent={{ side: "bottom" }}>
        {canBeEdited && editionSubMenu}
        {canBeDeleted && deletionSubMenu}
      </EllipsisMenu>
    )

    if (canBeEdited || canBeDeleted) {
      setCustomHeaderButtons([ellipsisMenu])
    }
  }, [])

  return (
    <div className={cn("flex w-full flex-col", className)} {...props}>
      {/* HEADER*/}
      <div
        className={cn(
          "flex h-73 w-full flex-col justify-between rounded-b-4xl bg-linear-to-r p-8 pt-20",
          theme === Theme.OwnerDark ? "from-secondary-100 to-secondary-300" : "from-secondary-700 to-secondary-500",
        )}
        {...props}
      >
        <div className="ml-2 flex w-fit items-center gap-2 rounded-full bg-surface-foreground/50 py-2 pr-3 pl-2.5">
          <ZapIcon size={20} color="currentColor" className="text-primary-500" />
          <Text fontStyle="LM" className="text-textDefault-body">
            {word("promoCode.view.tagLabel")}
          </Text>
        </div>
        <div className="flex flex-col gap-1">
          <Text fontStyle="HS" className="text-white">
            {shortenText(promoCode.title, 30)}
          </Text>
          <Text fontStyle="BM" className="text-white">
            {word("promoCode.view.codeToShare")}
          </Text>
          <div className="flex items-center gap-4">
            <Text fontStyle="HL" className="word-break-all text-white">
              {promoCode.code}
            </Text>
            <Files
              size={25}
              color="currentColor"
              className="text-white"
              onClick={() => {
                void copyCodeToClipboard()
              }}
            />
          </div>
        </div>
      </div>

      {/* CONTENT */}
      <div className="flex flex-col p-5">
        {/* INFORMATION */}
        <InformationContainer>
          <PromoCodeInformationRow
            label={word("promoCode.view.discount")}
            value={getFormattedPromoCodeReduction(
              promoCode.type,
              promoCode.value,
              language,
              box?.stripeAccountCurrency ?? user.stripeAccountCurrency ?? undefined,
            )}
            icon={<Percent />}
          />
          <PromoCodeInformationRow
            label={word("promoCode.view.startAt")}
            value={getFormattedPromoCodeStartAt(promoCode.startAt, promoCode.createdAt)}
            icon={<ClockAlert />}
          />
          <PromoCodeInformationRow
            label={word("promoCode.view.expirationDate")}
            value={getFormattedPromoCodeExpiration(promoCode.expirationDate ?? undefined)}
            icon={<Clock2 />}
          />
          <PromoCodeInformationRow
            label={word("promoCode.view.durationInMonths")}
            value={
              promoCode.durationInMonths
                ? `${promoCode.durationInMonths.toString()} ${word("global.month.label", { count: promoCode.durationInMonths })}`
                : word("promoCode.form.input.applicationDuration.value.unlimited")
            }
            icon={<ClockFading />}
          />
          <PromoCodeInformationRow
            label={word("promoCode.view.maxAttendees")}
            value={
              promoCode.maxAttendees
                ? `${promoCode.maxAttendees.toString()} ${word("global.customer.label", { count: promoCode.maxAttendees })}`
                : word("promoCode.form.input.maxAttendees.value.unlimited")
            }
            icon={<Users />}
          />
          <PromoCodeInformationRow
            label={
              promoCode.onlyNewCustomer
                ? word("promoCode.view.newCustomersOnly")
                : word("promoCode.view.everyCustomers")
            }
            icon={<Funnel />}
          />
        </InformationContainer>

        {promoCode instanceof PromoCodeEntity && promoCode.programs.length > 0 && (
          <ProgramContainer>
            <Text className="text-textDefault-body" fontStyle="LS" style={{ lineHeight: "21px" }}>
              {word("promoCode.form.input.relatedPrograms").toUpperCase()}
            </Text>
            {promoCode.programs.map((program) => (
              <ProgramReductionItem
                key={program.id}
                program={program}
                promoCodeType={promoCode.type}
                promoCodeValue={promoCode.value}
                promoCodeDuration={promoCode.durationInMonths ?? undefined}
              />
            ))}
          </ProgramContainer>
        )}

        {promoCode instanceof PromoCodeEntity && promoCode.programBundles.length > 0 && (
          <ProgramContainer>
            <Text className="text-textDefault-body" fontStyle="LS" style={{ lineHeight: "21px" }}>
              {word("promoCode.form.input.relatedProgramBundle").toUpperCase()}
            </Text>
            {promoCode.programBundles.map((program) => (
              <ProgramReductionItem
                key={program.id}
                program={program}
                promoCodeType={promoCode.type}
                promoCodeValue={promoCode.value}
                promoCodeDuration={promoCode.durationInMonths ?? undefined}
              />
            ))}
          </ProgramContainer>
        )}

        {promoCode instanceof BoxPromoCodeEntity && promoCode.boxOffers.length > 0 && (
          <ProgramContainer>
            <Text className="text-textDefault-body" fontStyle="LS" style={{ lineHeight: "21px" }}>
              {word("promoCode.form.input.relatedOffers").toUpperCase()}
            </Text>
            {promoCode.boxOffers.map((boxOffer) => (
              <BoxOfferReductionItem
                key={boxOffer.id}
                boxOffer={boxOffer}
                promoCodeType={promoCode.type}
                promoCodeValue={promoCode.value}
                promoCodeDuration={promoCode.durationInMonths ?? undefined}
              />
            ))}
          </ProgramContainer>
        )}
      </div>
    </div>
  )
}

function PromoCodeInformationRow({
  label,
  value,
  icon,
  className,
  ...props
}: {
  label: string
  value?: string
  icon?: React.ReactNode
} & React.ComponentProps<"div">) {
  return (
    <div className={cn("flex items-center gap-2", className)} {...props}>
      {icon ? icon : null}
      <Text fontStyle="BM" className="text-textDefault-caption">
        {label}
      </Text>
      <Text fontStyle="TM">{value}</Text>
    </div>
  )
}

const InformationContainer = ({ className, ...props }: React.ComponentProps<"div">) => (
  <div className={cn("flex flex-col gap-4", className)} {...props} />
)

const ProgramContainer = ({ className, ...props }: React.ComponentProps<"div">) => (
  <div className={cn("mt-5 pb-5", className)} {...props} />
)
